"use client";

import { useChain, useSpringRef } from "@react-spring/web";
import { Lookup, SpringRef } from "@react-spring/web";
import dynamic from "next/dynamic";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import React, { ReactElement, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import CookiePolicyButton from "@/app/shared-components/cookie-policy-button/cookie-policy-button";
import CountryChangeModal from "@/app/shared-components/country-change-modal/country-change-modal";
import QuestionFooterSection from "@/app/shared-components/question-footer-section";
import { useWizard } from "@/app/shared-providers/wizard-context-provider";
import QuestionsContainer from "@/components/organisms/question-section/questions-form-container";
import QuestionsSupportSection from "@/components/organisms/question-section/questions-support-section";
import { APPLICATION, DESKTOP_BREAKPOINT, PRODUCT_DOMAIN } from "@/constants";
import setCookieForHeader from "@/hooks/api/cookie/get-cookie";
import useGtmPageView from "@/hooks/gtm/use-gtm-page-view";
import useCustomPageTracking from "@/hooks/page-tracking/use-custom-page-tracking";
import useUpdateProgressBar from "@/hooks/progress/use-update-progress-bar";
import useMediaQuery from "@/hooks/use-media-query/use-media-query-enhanced";
import useStickyLayout from "@/hooks/use-sticky-layout/use-sticky-layout";
import QuestionsStateProvider from "@/providers/questions-state-provider";
import { GetQuoteFilter } from "@/store/slices/quote/API/getQuote";
import { getQuoteData } from "@/store/slices/quote/quote.action";
import { AppDispatch, RootState } from "@/store/store";
import { storageAvailable } from "@/utils/common.utils";
import { CoverTypeContent } from "@/utils/schema/cover-type-page-schema.utils";
import { CruiseContent } from "@/utils/schema/cruise-page-schema.utils";
import { LocationToContent } from "@/utils/schema/location-to-page-schema.utils";
import { OrganiserContent } from "@/utils/schema/organiser-page-schema.utils";
import { PartyTypeContent } from "@/utils/schema/party-type-page-schema.utils";
import { DateTypeContent } from "@/utils/schema/travel-date-page-schema.utils";
import { TravellersContent } from "@/utils/schema/travellers-page-schema.utils";

import withApplication from "../with-application";
import withQuestionQuoteData from "../with-quote-question-modified-data";

const Footer = dynamic(
  () => import("@/components/organisms/footer/footer-section"),
  {
    ssr: false,
  }
);

export type CommonPageContent =
  | (LocationToContent & { page: "travelling-to" })
  | (CoverTypeContent & { page: "cover-type" })
  | (CruiseContent & { page: "cruise" })
  | (DateTypeContent & { page: "travel-dates" })
  | (PartyTypeContent & { page: "cover-for" })
  | (OrganiserContent & { page: "organiser" })
  | (TravellersContent & { page: "traveller" });

type FormComponentProps = {
  pageContent: CommonPageContent;
  showondesktop?: string;
  showontab?: string;
  formRef?: SpringRef<Lookup>;
  ctaRef?: SpringRef<Lookup>;
};

type SupportComponentProps = {
  pageContent: CommonPageContent;
  showondesktop?: string;
  showontab?: string;
  loading?: boolean;
  supportRef?: SpringRef<Lookup>;
  shouldApplyStickyStyles?: boolean;
};

interface Props {
  helpButton: ReactElement;
  logo: ReactElement;
  pageName: string;
  pageContent: CommonPageContent;
  FormComponent?: React.ComponentType<FormComponentProps>;
  SupportComponent?: React.ComponentType<SupportComponentProps>;
  application?: "quote" | "renewal";
}

function PageLayoutWrapper({
  helpButton,
  logo,
  pageName,
  pageContent,
  FormComponent = QuestionsContainer,
  SupportComponent = QuestionsSupportSection,
  application = "quote",
}: Readonly<Props>) {
  const [footer, setFooter] = useState<boolean>(false);
  const [isdesktop] = useMediaQuery("(min-width: 600px)");
  const [isdesktopTab] = useMediaQuery("(min-width: 904px)");
  const { loading, referenceId } = useSelector(
    (state: RootState) => state.quote
  );
  const dispatch = useDispatch<AppDispatch>();
  const searchParams = useSearchParams();
  const quoteRef = searchParams.get("quoteId");
  const affiliate = searchParams.get("affiliate");

  const router = useRouter();

  const formContentSpringRef = useSpringRef();
  const formCtaSpringRef = useSpringRef();
  const supportContentSpringRef = useSpringRef();
  const pathname = usePathname();

  useChain(
    [formContentSpringRef, formCtaSpringRef, supportContentSpringRef],
    [0, 0.5, 1]
  );

  const { current, wizard } = useWizard();
  const { productSpecialFeatures } = wizard;
  useUpdateProgressBar(current?.progressBarPercentage ?? -1);

  const { pageReferrer, pageUrl, pageTitle } = useCustomPageTracking(pageName);

  useGtmPageView({
    userId: "",
    quoteId: "",
    pageReferrer,
    pageUrl,
    pageTitle,
  });

  useEffect(() => {
    if (typeof window !== "undefined" && window) {
      if (storageAvailable() && !sessionStorage.getItem("footer")) {
        setFooter(true);
      }
    }
  }, []);

  let getQuoteFilter: GetQuoteFilter = {
    affiliate: null,
    quoteId: null,
  };

  if (!affiliate && !quoteRef) {
    getQuoteFilter.affiliate = "quoteInit";
  } else {
    getQuoteFilter = {
      affiliate,
      quoteId: quoteRef,
    };
  }

  const fetchQuoteDataComponent = async () => {
    if (getQuoteFilter.affiliate && !referenceId) {
      if (pathname === "/") {
        await setCookieForHeader();
      }
      if (
        (storageAvailable() && sessionStorage.getItem("pageReload")) ||
        !sessionStorage.getItem("initialLoad")
      ) {
        sessionStorage.setItem("initialLoad", "true");
        sessionStorage.removeItem("pageReload");
        await dispatch(getQuoteData({ getQuoteFilter, dispatch, router }));
      }
    }
    if (APPLICATION === "renewal" && referenceId) {
      sessionStorage.removeItem("pageReload");
    }
  };

  useEffect(() => {
    fetchQuoteDataComponent();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const { stickyStyles, isStickyPage, screenWidth } = useStickyLayout();

  // Disable sticky styles specifically for STS quote and AVN quote
  const isStsQuote = PRODUCT_DOMAIN === "STS" && APPLICATION === "quote";
  const isAvnQuote = PRODUCT_DOMAIN === "AVN" && APPLICATION === "quote";
  const shouldApplyStickyStyles = !isStsQuote && !isAvnQuote;

  return (
    <QuestionsStateProvider>
      <div
        data-testid="questionPage-test-id"
        className="bg-surface-neutral-option-2"
      >
        {productSpecialFeatures?.showCountryFooter && <CountryChangeModal />}
        <div className="max-w-[1200px] mx-auto my-0 w-[100%] relative min-h-[calc(100svh-12px)] lg:pb-0 pb-spacing-5xl ">
          <main
            id="question-page"
            aria-label="question-page"
            className=" lg:py-spacing-xxl md:py-spacing-xl py-spacing-l"
          >
            <div
              className=" grid md:grid-cols-12 grid-cols-6 md:gap-spacing-l gap-spacing-m xl:mx-auto lg:mx-[72px] 
      md:mx-[48px] mx-[24px] relative"
            >
              <div className="lg:col-span-7 col-span-12 bg-surface-neutral-option-1 rounded-radius-xxl">
                <div className=" flex flex-col h-full">
                  <div
                    className={` text-surface-neutral-option-2 rounded-radius-xxl relative`}
                  >
                    {helpButton}
                    {logo}
                  </div>

                  <FormComponent
                    pageContent={pageContent}
                    showondesktop={isdesktop.toString()}
                    showontab={isdesktopTab.toString()}
                    formRef={formContentSpringRef}
                    ctaRef={formCtaSpringRef}
                  />
                </div>
              </div>

              <div
                className={` lg:col-span-5 col-span-12 lg:sticky top-0 bottom-0`}
                style={{
                  ...(isStickyPage &&
                  screenWidth >= DESKTOP_BREAKPOINT &&
                  shouldApplyStickyStyles
                    ? stickyStyles
                    : {}),
                }}
              >
                <SupportComponent
                  pageContent={pageContent}
                  showondesktop={isdesktop.toString()}
                  showontab={isdesktopTab.toString()}
                  loading={loading}
                  supportRef={supportContentSpringRef}
                  shouldApplyStickyStyles={shouldApplyStickyStyles}
                />
              </div>
            </div>
            <QuestionFooterSection />
            {productSpecialFeatures?.useNewHelpOverlay && (
              <CookiePolicyButton />
            )}
          </main>
          {footer && application === "quote" && (
            <footer data-testid="footer">
              <Footer />
            </footer>
          )}
        </div>
      </div>
    </QuestionsStateProvider>
  );
}

export default withQuestionQuoteData(withApplication(PageLayoutWrapper));
